import { Injectable } from '@angular/core';

/**
 * Service for generating device fingerprints.
 * Generates a unique fingerprint based on browser/device characteristics.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceFingerprintService {
  private cachedFingerprint: string | null = null;

  /**
   * Generates a device fingerprint from browser characteristics.
   * Uses a combination of:
   * - User Agent
   * - Screen resolution
   * - Timezone
   * - Language
   * - Platform
   * - Canvas fingerprint (if available)
   */
  async generateFingerprint(): Promise<string> {
    // Return cached fingerprint if available
    if (this.cachedFingerprint) {
      return this.cachedFingerprint;
    }

    const components: string[] = [];

    // User Agent
    if (navigator.userAgent) {
      components.push(`ua:${navigator.userAgent}`);
    }

    // Screen resolution
    if (screen.width && screen.height) {
      components.push(`screen:${screen.width}x${screen.height}`);
    }

    // Timezone
    if (Intl.DateTimeFormat) {
      components.push(`tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
    }

    // Language
    if (navigator.language) {
      components.push(`lang:${navigator.language}`);
    }

    // Platform
    if (navigator.platform) {
      components.push(`platform:${navigator.platform}`);
    }

    // Canvas fingerprint (more advanced, helps identify unique browsers)
    try {
      const canvasFingerprint = await this.getCanvasFingerprint();
      if (canvasFingerprint) {
        components.push(`canvas:${canvasFingerprint}`);
      }
    } catch (e) {
      // Canvas fingerprinting may fail in some browsers, ignore
      console.warn('[DeviceFingerprintService] Canvas fingerprint failed:', e);
    }

    // Combine all components and hash
    const fingerprintString = components.join('|');
    const hash = await this.hashString(fingerprintString);
    
    this.cachedFingerprint = hash;
    return hash;
  }

  /**
   * Gets canvas fingerprint (helps identify unique browsers)
   */
  private async getCanvasFingerprint(): Promise<string | null> {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        return null;
      }

      // Draw some text
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Device fingerprint', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Device fingerprint', 4, 17);

      // Get canvas data
      const dataUrl = canvas.toDataURL();
      
      // Hash the data URL
      return await this.hashString(dataUrl);
    } catch (e) {
      return null;
    }
  }

  /**
   * Hashes a string using Web Crypto API (SHA-256)
   */
  private async hashString(str: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(str);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      return hashHex;
    } catch (e) {
      // Fallback to simple hash if Web Crypto API is not available
      console.warn('[DeviceFingerprintService] Web Crypto API not available, using fallback');
      return this.simpleHash(str);
    }
  }

  /**
   * Simple hash fallback (for browsers without Web Crypto API)
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Clears cached fingerprint (useful for testing)
   */
  clearCache(): void {
    this.cachedFingerprint = null;
  }
}
